import { cn } from '@/lib/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BadgeProps {
  status: string;
  text?: string;
}

const COLOR_GROUPS = {
  green: {
    bg: 'bg-[#A9D3421A]',
    dot: 'bg-[#A9D342]',
  },
  lightGreen: {
    bg: 'bg-[#C0E57A1A]',
    dot: 'bg-[#C0E57A]',
  },
  red: {
    bg: 'bg-[#F871711A]',
    dot: 'bg-[#F87171]',
  },
  gray: {
    bg: 'bg-[#27272A]',
    dot: 'bg-[#A1A1AA]',
  },
  yellow: {
    bg: 'bg-[#FACC151A]',
    dot: 'bg-[#FACC15]',
  },
  blue: {
    bg: 'bg-[#60A5FA1A]',
    dot: 'bg-[#60A5FA]',
  },
  lightYellow: {
    bg: 'bg-[rgba(250,204,21,0.10)]',
    dot: 'bg-[#FDE047]',
  },
  orange: {
    bg: 'bg-[#FB923C1A]',
    dot: 'bg-[#FB923C]',
  },
  purple: {
    bg: 'bg-[#8B5CF61A]',
    dot: 'bg-[#8B5CF6]',
  },
} as const;

type ColorKey = keyof typeof COLOR_GROUPS;

const STATUS_COLOR_MAPPING: Record<string, ColorKey> = {
  active: 'green',
  enabled: 'green',
  completed: 'green',
  sell: 'green',
  transfer_success: 'green',
  paid: 'green',
  withdrawal: 'green',
  transfer: 'green',
  commission_in: 'green',
  receive_retention: 'green',
  receive_anticipation: 'green',
  return_withdrawal: 'green',
  retention_released: 'green',

  deposit: 'green',
  referral_reward_credit: 'green',
  partner_commission_credit: 'green',
  partner_commission_debit_reversed: 'green',
  cpa_commission: 'green',
  forced_retention_released: 'green',
  released: 'green',

  credited: 'green',
  receivable: 'lightGreen',
  anticipation: 'green',
  withdrawal_return: 'green',
  success: 'green',

  incomplete: 'blue',
  pending_authorizing: 'blue',

  blocked: 'red',
  rejected: 'red',
  failed: 'red',
  not_authorized: 'red',
  disapproved: 'red',
  refused: 'red',
  chargeback: 'purple',

  chargeback_fee: 'red',

  inactive: 'gray',
  disabled: 'gray',
  // Pixel desabilitado = vermelho (não cinza como "inactive" genérico) — sinaliza que o pixel
  // parou de enviar conversões, não é só um estado neutro.
  pixel_disabled: 'red',
  tax: 'gray',
  fee: 'gray',
  commission_out: 'gray',
  retention: 'gray',

  adjustment: 'gray',
  withdrawal_fee: 'gray',
  withdrawal_return_fee: 'gray',
  cpa_fee: 'gray',
  platform_fee: 'gray',
  acquirer_fee: 'gray',
  partner_commission_debit: 'gray',

  pending: 'yellow',
  awaiting_approval: 'yellow',
  '3ds_authenticated': 'yellow',
  pre_chargeback: 'yellow',
  forced_retention: 'yellow',
  retrying: 'yellow',
  revision: 'yellow',
  pending_refund: 'yellow',

  pre_chargeback_fee: 'blue',
  refund: 'blue',
  refunded: 'blue',
  processing: 'blue',
  analysis: 'blue',
  sent: 'blue',
  referral_reward_refunded: 'blue',
  partner_commission_refunded: 'blue',
  cpa_commission_revert: 'blue',
  reversed: 'blue',
  partially_reversed: 'yellow',
  internal_transfer: 'blue',

  scheduled: 'gray',
  canceled: 'gray',
  declined: 'red',

  awaiting_payment: 'lightYellow',

  delivered: 'green',
  bounced: 'orange',

  complained: 'red',

  pending_order: 'yellow',
  processing_order: 'blue',
  shipped: 'blue',
  in_transit: 'purple',
  out_for_delivery: 'purple',
  delivery_failed: 'red',
  cancelled_order: 'gray',
  returned: 'orange',
  manual: 'gray',
  expired: 'gray',
  revoked: 'red',

  past_due: 'orange',
  suspended: 'red',

  affiliate_risk_normal: 'green',
  affiliate_risk_medium: 'yellow',
  affiliate_risk_high: 'red',
};

const Badge: React.FC<BadgeProps> = ({ status, text }) => {
  const { t } = useTranslation('common');

  const colorKey = STATUS_COLOR_MAPPING[status] ?? 'gray';
  const colors = COLOR_GROUPS[colorKey];

  const { container, dot } = {
    container: `${colors.bg} text-white`,
    dot: colors.dot,
  };

  return (
    <div
      className={cn(
        'flex items-center space-x-1 px-2 py-1 rounded-full w-max text-xs font-medium',
        container
      )}
    >
      <span className={cn('w-2 h-2 rounded-full', dot)}></span>
      <span>{text || t(status)}</span>
    </div>
  );
};

export default Badge;
